import { splitE164Phone } from './phoneNumber'
import type { STRINGS_NLS } from '../nls/strings_nls'

export const WINDOW_DURATION_MS = 24 * 60 * 60 * 1000

/**
 * Mask an E.164 phone number, revealing only the country code and last 4 digits.
 * Example: `+919876543210` -> `+91 ••••• •3210`.
 * Full numbers are never rendered in the UI.
 */
export const maskWhatsappNumber = (e164: string | null | undefined): string => {
  if (!e164 || typeof e164 !== 'string') return ''
  const trimmed = e164.trim()
  if (!trimmed) return ''
  const { countryCode, localNumber } = splitE164Phone(trimmed)
  const last4 = localNumber.slice(-4)
  if (!last4) return countryCode ? `${countryCode} •••••` : ''
  return `${countryCode} ••••• •${last4}`
}

export type ReplyWindowResult = {
  open: boolean
  msLeft: number
}

/**
 * Computes whether the 24-hour reply window from the last inbound message is open.
 * Returns `{ open: false, msLeft: 0 }` if `lastInboundAt` is missing or invalid.
 */
export const replyWindow = (
  lastInboundAt: unknown,
  now: number | Date = Date.now(),
): ReplyWindowResult => {
  if (!lastInboundAt) {
    return { open: false, msLeft: 0 }
  }

  let inboundMs = NaN
  if (typeof lastInboundAt === 'number') {
    inboundMs = lastInboundAt
  } else if (lastInboundAt instanceof Date) {
    inboundMs = lastInboundAt.getTime()
  } else if (typeof lastInboundAt === 'object' && lastInboundAt !== null) {
    const raw = lastInboundAt as Record<string, unknown>
    if (typeof (raw as { toMillis?: () => number }).toMillis === 'function') {
      inboundMs = (raw as { toMillis: () => number }).toMillis()
    } else if (typeof (raw as { toDate?: () => Date }).toDate === 'function') {
      inboundMs = (raw as { toDate: () => Date }).toDate().getTime()
    } else if (typeof raw.seconds === 'number') {
      const nanos = typeof raw.nanoseconds === 'number' ? raw.nanoseconds : 0
      inboundMs = raw.seconds * 1000 + Math.floor(nanos / 1e6)
    }
  } else if (typeof lastInboundAt === 'string') {
    const parsed = new Date(lastInboundAt).getTime()
    if (!Number.isNaN(parsed)) {
      inboundMs = parsed
    }
  }

  if (!Number.isFinite(inboundMs)) {
    return { open: false, msLeft: 0 }
  }

  const nowMs = typeof now === 'number' ? now : (now instanceof Date ? now.getTime() : Date.now())
  const elapsed = nowMs - inboundMs
  const msLeft = Math.max(0, WINDOW_DURATION_MS - elapsed)
  const open = msLeft > 0 && elapsed >= 0

  return {
    open,
    msLeft: open ? msLeft : 0,
  }
}

/**
 * Returns true if the channel has `identityType === 'WHATSAPP'`.
 */
export const isWhatsappChannel = (channel: unknown): boolean => {
  if (!channel || typeof channel !== 'object') return false
  return (channel as { identityType?: unknown }).identityType === 'WHATSAPP'
}

export type WhatsappTickKind =
  | 'sending'
  | 'sent'
  | 'delivered'
  | 'read'
  | 'held'
  | 'paused'
  | 'failed'

export type WhatsappStatusViewResult = {
  tickKind: WhatsappTickKind
  nlsKey: keyof typeof STRINGS_NLS
}

/**
 * Maps each message status to its corresponding delivery tick kind and accessible NLS string key.
 */
export const whatsappStatusView = (status: string | undefined): WhatsappStatusViewResult => {
  switch (status) {
    case 'SENDING':
      return { tickKind: 'sending', nlsKey: 'NLSChatStatusSending' }
    case 'SENT':
      return { tickKind: 'sent', nlsKey: 'NLSChatStatusSent' }
    case 'DELIVERED':
      return { tickKind: 'delivered', nlsKey: 'NLSChatStatusDelivered' }
    case 'READ':
      return { tickKind: 'read', nlsKey: 'NLSChatStatusRead' }
    case 'HELD_OUTSIDE_WINDOW':
      return { tickKind: 'held', nlsKey: 'NLSChatStatusHeldOutsideWindow' }
    case 'PAUSED':
      return { tickKind: 'paused', nlsKey: 'NLSChatStatusPaused' }
    case 'FAILED':
    default:
      return { tickKind: 'failed', nlsKey: 'NLSChatStatusFailedToSend' }
  }
}
